import { useEffect, useMemo, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';

interface Props {
  x: number[];
  y: (number | null)[];
  color: string;
  fillAlpha?: number;
  height?: number;
  yLabel?: string;
  xLabel?: string;
  yFormat?: (v: number) => string;
  xFormat?: (v: number) => string;
  /** Invert the y-axis (smaller values up). Useful for pace, where faster = better. */
  invertY?: boolean;
}

export function Sparkline({
  x,
  y,
  color,
  fillAlpha = 0.18,
  height = 128,
  yLabel,
  xLabel,
  yFormat,
  xFormat,
  invertY = false
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);

  const data = useMemo<uPlot.AlignedData>(() => [x, y], [x, y]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;

    const opts: uPlot.Options = {
      width: el.clientWidth || 400,
      height,
      padding: [8, 12, 8, 12],
      cursor: {
        drag: { x: false, y: false },
        points: { size: 6 }
      },
      scales: {
        x: { time: false },
        y: invertY ? { dir: -1 } : {}
      },
      axes: [
        {
          stroke: '#A6ADB8',
          grid: { show: false },
          ticks: { show: false },
          size: 22,
          values: (_u, splits) => splits.map((s) => (xFormat ? xFormat(s) : String(s))),
          font: '10px Inter, system-ui, sans-serif',
          label: xLabel,
          labelSize: 14,
          labelFont: '10px Inter, system-ui, sans-serif'
        },
        {
          stroke: '#A6ADB8',
          grid: { stroke: 'rgba(11, 13, 16, 0.05)', width: 1 },
          ticks: { show: false },
          size: 38,
          values: (_u, splits) => splits.map((s) => (yFormat ? yFormat(s) : String(s))),
          font: '10px Inter, system-ui, sans-serif',
          label: yLabel,
          labelSize: 14,
          labelFont: '10px Inter, system-ui, sans-serif'
        }
      ],
      series: [
        {},
        {
          stroke: color,
          width: 1.75,
          fill: hexToRgba(color, fillAlpha),
          points: { show: false }
        }
      ],
      legend: { show: false }
    };

    plotRef.current = new uPlot(opts, data, el);

    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      plotRef.current?.setSize({ width: w, height });
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      plotRef.current?.destroy();
      plotRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    plotRef.current?.setData(data);
  }, [data]);

  return <div ref={containerRef} style={{ width: '100%', height }} />;
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
